import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Mail, BarChart3, ShoppingBag, Users, FileText, Lock } from 'lucide-react';

/**
 * Shared "What you get with a Care Plan" admin-panel showcase.
 * Used in StepCareHosting (when user toggles 'No care plan'), the landing
 * AdminPanelUSP CTA, and the PricingPage hero.
 *
 * Variants:
 *   - default: full feature grid + dual CTAs (used on landing / pricing)
 *   - compact: tighter card optimised for the project builder warning area
 */
const FEATURES = [
  { icon: Mail, label: 'Live enquiries inbox', hint: '12 leads this week' },
  { icon: BarChart3, label: 'Realtime analytics', hint: '+18% MoM visitors' },
  { icon: ShoppingBag, label: 'Shop & orders', hint: 'In-house fulfilment' },
  { icon: FileText, label: 'Inline blog editor', hint: 'TipTap rich text' },
  { icon: Users, label: 'Staff roles (RBAC)', hint: 'Per-page permissions' },
  { icon: Lock, label: 'Self-serve edits', hint: 'No emails, no waiting' },
];

export default function AdminPanelShowcase({ variant = 'default', className = '' }) {
  if (variant === 'compact') {
    return (
      <div
        data-testid="admin-showcase-compact"
        className={`relative overflow-hidden rounded-2xl border border-[#00E5FF]/30 bg-gradient-to-br from-[#00E5FF]/5 via-transparent to-violet-500/5 p-5 ${className}`}
      >
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#00E5FF]/10 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1 bg-[#00E5FF]/15 text-[#00E5FF] px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest">
              <Sparkles className="w-3 h-3" /> What you'd be missing
            </span>
          </div>
          <h4 className="font-['Outfit'] text-base font-bold text-[#0A0A0A] dark:text-white mb-1">
            A live SaaS admin panel — included free with every Care Plan
          </h4>
          <p className="text-[12px] text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
            Edit content, see traffic, manage orders, invite staff — all in one place. No emails, no waiting, no per-change fees.
          </p>
          <div className="grid grid-cols-2 gap-1.5 mb-4">
            {FEATURES.slice(0, 6).map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-1.5 text-[11px] text-slate-700 dark:text-slate-300">
                <Icon className="w-3 h-3 text-[#00E5FF] flex-shrink-0" />
                <span className="truncate">{label}</span>
              </div>
            ))}
          </div>
          <Link
            to="/admin-demo"
            data-testid="showcase-demo-cta"
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1.5 bg-[#00E5FF] text-[#0A0A0A] px-4 py-2 rounded-full text-[12px] font-bold hover:bg-white transition-colors"
          >
            Open the live demo
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-showcase" className={`relative overflow-hidden rounded-3xl border border-slate-200 dark:border-white/10 bg-gradient-to-br from-[#00E5FF]/[0.06] via-white dark:via-transparent to-violet-500/[0.06] dark:to-violet-500/[0.06] p-6 sm:p-10 ${className}`}>
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#00E5FF]/15 dark:bg-[#00E5FF]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-violet-500/15 dark:bg-violet-500/10 blur-3xl pointer-events-none" />

      <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
        <div className="lg:col-span-2">
          <span className="inline-flex items-center gap-1.5 bg-[#00E5FF]/15 text-[#0095a8] dark:text-[#00E5FF] px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-4">
            <Sparkles className="w-3 h-3" /> Sales-tool · Live
          </span>
          <h3 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-[#0A0A0A] dark:text-white tracking-tight mb-3">
            See the admin panel<br/>your competitors don't have.
          </h3>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed mb-6">
            Every UI Designs Care Plan ships with a full SaaS admin panel — analytics, enquiries, blog editor, shop, RBAC. No screenshots: open the live demo and click around.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/admin-demo"
              data-testid="showcase-demo-cta-primary"
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-6 py-3 rounded-full text-sm font-bold hover:bg-[#0A0A0A] hover:text-[#00E5FF] transition-colors"
            >
              Open live demo
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 border border-slate-300 dark:border-white/15 text-[#0A0A0A] dark:text-white px-6 py-3 rounded-full text-sm font-semibold hover:border-[#00E5FF]/60 hover:text-[#0095a8] dark:hover:text-[#00E5FF] transition-all"
            >
              See Care Plans
            </Link>
          </div>
        </div>

        <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {FEATURES.map(({ icon: Icon, label, hint }) => (
            <div
              key={label}
              className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-4 hover:border-[#00E5FF]/40 hover:bg-[#00E5FF]/[0.04] dark:hover:bg-white/[0.05] transition-all group shadow-sm dark:shadow-none"
            >
              <div className="w-9 h-9 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center mb-3 group-hover:bg-[#00E5FF]/20 transition-colors">
                <Icon className="w-4 h-4 text-[#0095a8] dark:text-[#00E5FF]" />
              </div>
              <p className="font-['Outfit'] text-sm font-semibold text-[#0A0A0A] dark:text-white mb-0.5">{label}</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-500">{hint}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
